import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabaseAdmin } from '../lib/supabaseClient'
import { AdminUser, AuthResponse } from '../types/user'

interface AdminAuthContextType {
  user: AdminUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<AuthResponse>
  signOut: () => Promise<void>
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}

interface AdminAuthProviderProps {
  children: ReactNode
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Chaves de cache para o painel admin
  const ADMIN_USER_KEY = 'aguiar_admin_user'
  const ADMIN_EXP_KEY = 'aguiar_admin_exp'

  // Verificar se o usuário tem permissão de admin (para verificação de sessão existente)
  const verifyAdminUser = React.useCallback(async (email: string, expiresAtMs?: number) => {
    try {
      console.log('🔍 [AdminAuth] Verificando permissões de admin para:', email)
      
      // Verificar se já temos usuário para evitar verificações desnecessárias
      if (user && user.email === email) {
        console.log('✅ [AdminAuth] Usuário já verificado, pulando verificação')
        setLoading(false)
        return
      }
      
      console.log('🔍 [AdminAuth] Fazendo consulta no Supabase para:', email)
      
      // Testar se o cliente Supabase está funcionando
      console.log('🔍 [AdminAuth] Cliente Supabase configurado:', !!supabaseAdmin)
      
      // Adicionar timeout para a consulta
      const queryPromise = supabaseAdmin
        .from('usuario')
        .select('id, email, nome, empresa_nome, admin, ativo, is_teste')
        .eq('email', email)
        .single()
      
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Timeout na consulta')), 10000)
      )
      
      const result = await Promise.race([queryPromise, timeoutPromise]) as any
      const { data: userData, error: userError } = result

      console.log('🔍 [AdminAuth] Resultado da consulta:', { userData, userError })

      if (userError) {
        console.error('❌ [AdminAuth] Erro ao buscar dados do usuário:', userError)
        setUser(null)
        setLoading(false)
        return
      }

      console.log('🔍 [AdminAuth] Dados do usuário recebidos:', userData)

      if (userData && userData.admin === true && userData.ativo === true) {
        console.log('✅ [AdminAuth] Usuário tem permissão de admin, definindo usuário...')
        const adminUser: AdminUser = {
          id: userData.id,
          email: userData.email || '',
          role: 'admin',
          admin: true,
          nome: userData.nome,
          empresa_nome: userData.empresa_nome
        }
        
        console.log('✅ [AdminAuth] AdminUser criado:', adminUser)
        setUser(adminUser)
        setLoading(false)
        console.log('✅ [AdminAuth] Usuário definido e loading false')
      } else {
        console.log('❌ [AdminAuth] Usuário não tem permissão de admin ou não está ativo')
        console.log('❌ [AdminAuth] Dados recebidos:', {
          admin: userData?.admin,
          ativo: userData?.ativo,
          adminType: typeof userData?.admin,
          ativoType: typeof userData?.ativo
        })
        setUser(null)
        setLoading(false)
        console.log('❌ [AdminAuth] Usuário não é admin, loading false')
      }
    } catch (error) {
      console.error('💥 [AdminAuth] Erro ao verificar permissão de admin:', error)
      setUser(null)
      setLoading(false)
    }
  }, [user])

  // Verificar sessão existente e configurar listener
  useEffect(() => {
    console.log('🔐 [AdminAuth] Provider iniciado, verificando sessão...')
    
    let isMounted = true

    // Hidratar rapidamente do localStorage para evitar redirecionamento ao login em refresh
    try {
      const cached = localStorage.getItem(ADMIN_USER_KEY)
      const expStr = localStorage.getItem(ADMIN_EXP_KEY)
      const exp = expStr ? parseInt(expStr, 10) : undefined
      if (cached) {
        const cachedUser = JSON.parse(cached) as AdminUser
        if (!exp || Date.now() < exp) {
          setUser(cachedUser)
          setLoading(false)
        } else {
          localStorage.removeItem(ADMIN_USER_KEY)
          localStorage.removeItem(ADMIN_EXP_KEY)
        }
      }
    } catch {}
    
    // Verificar sessão existente
    const checkSession = async () => {
      try {
        console.log('🔍 [AdminAuth] Verificando sessão existente...')
        const { data: { session }, error } = await supabaseAdmin.auth.getSession()
        
        console.log('🔍 [AdminAuth] Resultado getSession:', { session, error })
        
        if (!isMounted) return
        
        if (session?.user?.email) {
          console.log('✅ [AdminAuth] Sessão encontrada para usuário:', session.user.email)
          const expMs = session.expires_at ? session.expires_at * 1000 : undefined
          await verifyAdminUser(session.user.email, expMs)
        } else {
          console.log('ℹ️ [AdminAuth] Nenhuma sessão encontrada')
          if (isMounted) setLoading(false)
        }
      } catch (error) {
        console.error('💥 [AdminAuth] Erro ao verificar sessão:', error)
        if (isMounted) setLoading(false)
      }
    }

    checkSession()

    // Listener para mudanças de autenticação
    console.log('👂 [AdminAuth] Configurando listener de autenticação...')
    const { data: { subscription } } = supabaseAdmin.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 [AdminAuth] Evento de autenticação:', event, session?.user?.email)
        
        if (!isMounted) return
        
        if (event === 'SIGNED_IN' && session?.user?.email) {
          console.log('✅ [AdminAuth] Usuário fez login, verificando permissões...')
          await verifyAdminUser(session.user.email)
        } else if (event === 'SIGNED_OUT') {
          console.log('🚪 [AdminAuth] Usuário fez logout')
          setUser(null)
          setLoading(false)
        } else if (event === 'TOKEN_REFRESHED' && session?.user?.email) {
          console.log('🔄 [AdminAuth] Token renovado, verificando permissões...')
          await verifyAdminUser(session.user.email)
        }
      }
    )

    return () => {
      console.log('🧹 [AdminAuth] Limpando listener de autenticação...')
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signIn = React.useCallback(async (email: string, password: string): Promise<AuthResponse> => {
    try {
      console.log('🔐 [AdminAuth] Tentando fazer login com:', email)
      
      const { data, error } = await supabaseAdmin.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        console.error('❌ [AdminAuth] Erro no login:', error.message)
        return { success: false, error: error.message }
      }

      if (data.user) {
        console.log('✅ [AdminAuth] Login bem-sucedido, retornando sucesso...')
        // A verificação de permissões será feita pelo listener onAuthStateChange
        return { success: true, user: undefined }
      }

      return { success: false, error: 'Falha na autenticação' }
    } catch (error) {
      console.error('💥 [AdminAuth] Erro geral no login:', error)
      return { success: false, error: 'Erro interno do servidor' }
    }
  }, [])

  const signOut = React.useCallback(async () => {
    try {
      console.log('🚪 [AdminAuth] Fazendo logout...')
      await supabaseAdmin.auth.signOut()
      setUser(null)
      console.log('✅ [AdminAuth] Logout realizado com sucesso')
    } catch (error) {
      console.error('❌ [AdminAuth] Erro ao fazer logout:', error)
    }
  }, [])

  const value: AdminAuthContextType = React.useMemo(() => ({
    user,
    loading,
    signIn,
    signOut
  }), [user, loading, signIn, signOut])

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}
